import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, Plus, ScrollText, Wallet } from 'lucide-react';
import { useSoldeReel } from '@/hooks/queries/useSoldeReel';
import { cn } from '@/lib/utils';

interface Props {
  entrepriseId: string | null;
  entrepriseLabel?: string | null;
}

const MOIS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

function formatDateFR(d: Date): string {
  return `${d.getDate()} ${(MOIS_FR[d.getMonth()] ?? '').toLowerCase()} ${d.getFullYear()}`;
}

function formatDateISOToFR(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function formatMontantDA(n: number): string {
  return (
    Math.round(n)
      .toLocaleString('fr-FR', { maximumFractionDigits: 0 })
      .replace(/[\u00A0\u202F\u2009]/g, ' ') + ' DA'
  );
}

type Mode = 'reel' | 'historique';

export function CaisseDashboardSimple({ entrepriseId, entrepriseLabel }: Props): JSX.Element {
  const [mode, setMode] = useState<Mode>('reel');
  const { data, isLoading } = useSoldeReel(entrepriseId);

  const today = useMemo(() => new Date(), []);
  const moisLabel = `${MOIS_FR[today.getMonth()]} ${today.getFullYear()}`;
  const dateAujourdhui = formatDateFR(today);

  const totalReel = data?.soldeReelTotal ?? 0;
  const totalHistorique = data?.soldeHistoriqueTotal ?? 0;
  const totalAffiche = mode === 'reel' ? totalReel : totalHistorique;
  const negatif = totalAffiche < 0;
  const nbCaisses = (data?.details ?? []).filter((d) => d.compte_type === 'caisse').length;
  const nbComptes = (data?.details ?? []).filter((d) => d.compte_type === 'banque').length;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-orange-50 to-amber-100 p-6 shadow-xl shadow-slate-900/5 sm:p-8"
      aria-label="Dashboard solde caisse"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-amber-300/40 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-orange-300/40 opacity-50 blur-3xl"
      />

      <div className="relative">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Calendar className="h-4 w-4 text-orange-600" />
            Mois en cours : <span className="font-bold text-slate-900">{moisLabel}</span>
          </div>
          <Link
            to="/finances/caisse/cloture"
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-orange-600 px-3 text-xs font-semibold text-white shadow-sm hover:bg-orange-700"
          >
            <Plus className="h-3.5 w-3.5" /> Créer une clôture / comptage
          </Link>
        </div>

        <div className="mb-3 inline-flex rounded-lg border border-orange-200 bg-white/60 p-0.5 backdrop-blur">
          <button
            type="button"
            onClick={() => setMode('reel')}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              mode === 'reel'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-slate-700 hover:bg-orange-100',
            )}
          >
            Solde réel
          </button>
          <button
            type="button"
            onClick={() => setMode('historique')}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
              mode === 'historique'
                ? 'bg-orange-600 text-white shadow-sm'
                : 'text-slate-700 hover:bg-orange-100',
            )}
          >
            Solde historique
          </button>
        </div>

        <div className="flex items-baseline gap-3">
          <Wallet className="h-7 w-7 shrink-0 text-orange-600" />
          <span className="text-xs font-bold uppercase tracking-widest text-slate-600 sm:text-sm">
            {mode === 'reel' ? 'Solde réel disponible' : 'Solde comptable historique'}
          </span>
        </div>

        <div
          className={cn(
            'mt-1 break-words text-4xl font-black tabular-nums leading-tight sm:text-5xl',
            isLoading ? 'text-slate-400' : negatif ? 'text-rose-700' : 'text-slate-900',
          )}
        >
          {isLoading ? '—' : formatMontantDA(totalAffiche)}
        </div>

        <div className="mt-2 text-xs text-slate-700">
          {mode === 'reel' ? (
            data?.hasAnyCloture ? (
              <>
                Basé sur la dernière clôture du{' '}
                <strong>{formatDateISOToFR(data.derniereClotureGlobale)}</strong> + opérations
                postérieures
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 font-medium text-amber-800">
                <AlertTriangle className="h-3 w-3" /> Aucune clôture trouvée — solde calculé depuis
                solde initial + toutes les opérations
              </span>
            )
          ) : (
            <>Total opérations validées + comptabilisées depuis le solde initial</>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600">
          <span>
            Au <strong>{dateAujourdhui}</strong>
          </span>
          {entrepriseLabel && (
            <span>
              · Périmètre : <strong>{entrepriseLabel}</strong>
            </span>
          )}
          {!isLoading && (
            <span>
              · {nbCaisses} caisse{nbCaisses > 1 ? 's' : ''} + {nbComptes} compte
              {nbComptes > 1 ? 's' : ''} bancaire{nbComptes > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {mode === 'reel' && !isLoading && data && data.hasAnyCloture && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-white/70 px-2.5 py-1 text-[11px] text-slate-600 backdrop-blur">
            <ScrollText className="h-3 w-3 text-slate-500" />
            Solde historique comptable :{' '}
            <span className="font-mono font-semibold text-slate-700">
              {formatMontantDA(totalHistorique)}
            </span>
          </div>
        )}

        {!isLoading && (data?.details ?? []).length === 0 && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
            ⚠ Aucune caisse ni compte bancaire actif pour cette entreprise.
          </div>
        )}
      </div>
    </section>
  );
}
